import { NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ChartPieSlice, FileText, SignOut, Gear, Database } from '@phosphor-icons/react'
import { memo, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui'
import { cn } from '@/lib/utils'
import SVGLogo from '@/assets/logo.svg'
import { useLogout } from '@/feature/auth/api'
import { useMetaData } from '@/lib/hooks/useMetaData'
import { useDebugModeStore } from '@/global/store'
import { Switch } from '@/components/ui/switch'
import { LayoutContainer } from '@/components/shared/Container'
import { PostHogPageView } from '@/feature/posthog/page-view'
import { ChatWidget } from '@/feature/support-chat/components/ChatWidget'
import { Show } from '@/components/shared/show'
import { useSettingsStore } from '@/feature/settings/store/useOrganizationStore'
import { useFinancialModelSettingsStore } from '@/feature/financial-model/settings/store/financialModelSettingsStore'

/* const SettingsDropdown = () => {
  const organizationId = useSettingsStore(state => state.organization?.id)
  const organizationName = useSettingsStore(state => state.organization?.name)
  const email = useAuthStore(state => state.user?.email)
  const workspaceName = useSettingsStore(state => state.active_workspace.name)
  const location = useLocation()
  const { pathname } = location
  const [isOpen, setIsOpen] = useState(false)
  const logout = useLogout()
  const navigate = useNavigate()
  const handleLogout = useCallback(() => {
    logout()
    navigate(`/auth/login`)
  }, [logout, navigate])
  const { t } = useTranslation(`common`)
  if (!organizationId) return null

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <li className='flex w-full items-center justify-between px-3 text-xs text-white'>
          {organizationName ?? `Organization`}
          <CaretDown size={16} className={cn(`transition-transform duration-300`, isOpen && `rotate-180`)} />
        </li>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='px-3 pb-3 pt-2'>
        <DropdownMenuLabel className='px-0'>{organizationName ?? `Organization`}</DropdownMenuLabel>
        <p className='text-xs text-secondary'>Launch Plan</p>
        <NavLink to='/dashboard/settings' onClick={() => setIsOpen(false)} state={{ from: pathname }}>
          <Button variant='secondary' className='my-4 w-full items-center justify-center gap-1' size='sm'>
            <Gear size={16} />
            {t(`layout.sidebar.settings`)}
          </Button>
        </NavLink>
        <DropdownMenuSeparator />
        <DropdownMenuGroup className='my-4 flex flex-col gap-2'>
          <p className='text-xs text-secondary'>{email}</p>
          <Show when={!!workspaceName}>
            <div className='flex items-center justify-between gap-2'>
              <p className='text-xs'>{workspaceName}</p>
              <Check size={16} />
            </div>
          </Show>
        </DropdownMenuGroup>
        <DropdownMenuSeparator className='mb-4' />
        <DropdownMenuItem onClick={handleLogout}>Logout</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
} */

export const Sidebar = memo(() => {
  const debugMode = useDebugModeStore()
  const { t } = useTranslation(`common`)
  const { pathname } = useLocation()
  const logout = useLogout()
  const navigate = useNavigate()
  const handleLogout = useCallback(() => {
    logout()
    navigate(`/auth/login`)
  }, [logout, navigate])

  const metaTitle = useMetaData()

  const hasFinancialModel = useFinancialModelSettingsStore(state => Object.keys(state.settings).length > 0)
  const organizationName = useSettingsStore(state => state.organization?.name)

  return (
    <>
      <div className='h-screen w-full max-w-[186px] bg-black'>
        <div className='h-2' />
        <div className='flex h-full flex-col'>
          <ul className='pt-4'>
            <NavLink to='/'>
              <li className='px-4'>
                <img width={20} height={20} src={SVGLogo} alt='logo' />
              </li>
            </NavLink>
            <div className='mt-11 flex w-full justify-center'>
              <p className='w-full px-3 text-xs text-white'>{organizationName ?? `Organization`}</p>
              {/* <SettingsDropdown /> */}
            </div>
            <div className='mt-4'>
              <NavigationItem to='/dashboard' pathname={pathname}>
                {({ isActive }) => (
                  <li className='flex items-center gap-3'>
                    <ChartPieSlice weight={isActive ? `fill` : `bold`} size={16} />
                    {t(`layout.sidebar.model`)}
                  </li>
                )}
              </NavigationItem>
            </div>
            {/* <div className="mt-4">
            <NavigationItem to="/dashboard/market" disabled>
            {({ isActive }) => (
              <>
              <Graph weight={isActive ? `fill` : `bold`} size={16} />
              {t(`layout.sidebar.market`)}
              </>
              )}
              </NavigationItem>
              </div> */}
            <div className='mt-2'>
              <NavigationItem to='/dashboard/report' pathname={pathname} disabled={!hasFinancialModel}>
                {({ isActive }) => (
                  <li className='flex items-center gap-3'>
                    <FileText weight={isActive ? `fill` : `bold`} size={16} />
                    {t(`layout.sidebar.report`)}
                  </li>
                )}
              </NavigationItem>
            </div>
            {(import.meta.env.VITE_NODE_ENV === `development` || import.meta.env.VITE_NODE_ENV === `staging`) && (
              <div className='mt-2'>
                <NavigationItem to='/dashboard/dataset' pathname={pathname} disabled={!hasFinancialModel}>
                  {({ isActive }) => (
                    <li className='flex items-center gap-3'>
                      <Database weight={isActive ? `fill` : `bold`} size={16} />
                      {t(`layout.sidebar.dataset`)}
                    </li>
                  )}
                </NavigationItem>
              </div>
            )}
            {/* <div className="mt-2">
              <NavigationItem
                to="/dashboard/fundraising"
                pathname={pathname}
                disabled={!hasFinancialModel}
              >
                {({ isActive }) => (
                  <li className="flex items-center gap-3">
                    <HandCoins weight={isActive ? `fill` : `bold`} size={16} />
                    {t(`layout.sidebar.fundraising`)}
                  </li>
                )}
              </NavigationItem>
            </div> */}
          </ul>
          <div className='mb-4 mt-auto'>
            <Show when={import.meta.env.VITE_DISABLE_DEBUG_MODE !== `true`}>
              <div className='mb-4 flex items-center gap-2 px-8'>
                <span className='text-sm text-white'>Test Mode</span>
                <Switch
                  checked={debugMode.mode === `test`}
                  onCheckedChange={() => {
                    debugMode.setMode(debugMode.mode === `test` ? `production` : `test`)
                  }}
                />
              </div>
            </Show>
            <div className='mb-4 ml-8'>
              <NavLink to='/dashboard/settings' state={{ from: pathname }}>
                <li className='flex items-center gap-2 text-xs text-white hover:font-medium'>
                  <Gear size={16} />
                  {t(`layout.sidebar.settings`)}
                </li>
              </NavLink>
            </div>
            <hr className='myt-2 border-white/20' />
            <div className='px-4'>
              <Button variant='ghost' onClick={handleLogout} className='text-secondary hover:bg-transparent hover:text-white'>
                <SignOut size={16} className='mr-2' />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>
      <title>{metaTitle.title}</title>
      <PostHogPageView />
    </>
  )
})

const INDIPENDENT_ROUTES = [`/dashboard/fundraising`, `/dashboard/report`, `/dashboard/workspace`, `/dashboard/dataset`]

const NavigationItem = (props: {
  to: string
  children: ({ isActive }: { isActive: boolean }) => React.ReactNode
  disabled?: boolean
  pathname: string
}) => {
  const isEqual = useMemo(() => {
    const isIndependent = INDIPENDENT_ROUTES.find(route => props.to.startsWith(route))
    if (isIndependent) {
      return props.pathname.includes(props.to)
    }

    return !INDIPENDENT_ROUTES.some(route => props.pathname.startsWith(route)) && props.pathname.includes(props.to)
  }, [props.pathname, props.to])
  return (
    <NavLink
      to={props.to}
      className={cn(
        isEqual ? `bg-gray-700 hover:font-medium` : `text-white`,
        `mx-auto flex h-12 w-full max-w-[154px] items-center gap-3 rounded-lg p-4 text-xs text-white hover:bg-gray-700 hover:font-medium`,
        props.disabled && `cursor-not-allowed opacity-50`
      )}
      onClick={e => {
        if (props.disabled) {
          e.preventDefault()
        }
      }}
    >
      {props.children({ isActive: isEqual })}
    </NavLink>
  )
}

type AppLayoutProps =
  | {
      withChildren?: true
      children?: React.ReactNode
    }
  | {
      withChildren?: false
      children?: never
    }

export const AppLayout = memo((props: AppLayoutProps) => (
  <div className='flex overflow-hidden'>
    <Sidebar />
    <LayoutContainer>{props.withChildren ? props.children : <Outlet />}</LayoutContainer>
    <ChatWidget />
  </div>
))

AppLayout.displayName = `AppLayout`
